import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { PDFViewer } from "@react-pdf/renderer";
import { tokens } from "../../../theme";
import {
  listHotelInvoicesDetails,
  resetHotelInvoicesDetails,
  updateHotelInvoicesDetails,
} from "../../../store/actions";
import InvoicePDF from "./InvoicePDF";
import SettlementDialog from "./SettlementDialog";
import moment from "moment";

export default function BookingBillDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [showPreview, setShowPreview] = useState(false);
  const [showSettlement, setShowSettlement] = useState(false);
  const [editedRoomCharges, setEditedRoomCharges] = useState([]);
  const [totals, setTotals] = useState({
    amount: 0,
    sgst: 0,
    cgst: 0,
    total: 0,
  });
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [customer, setCustomer] = useState({});
  const [bookingDetails, setBookingDetails] = useState({
    checkin_date: "",
    checkout_date: "",
    booking_id: "",
    from: "",
    inv_no: "",
    paid_amount: 0,
  });

  const { hotelInvoivesDetails, hotelProfile } = useSelector((state) => ({
    hotelInvoivesDetails: state.admin.hotelInvoivesDetails,
    hotelProfile: state.registration.hotelProfile,
  }));

  useEffect(() => {
    document.title = "Booking Invoice Details";
    if (id) {
      dispatch(resetHotelInvoicesDetails());
      dispatch(listHotelInvoicesDetails({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (hotelInvoivesDetails) {
      const formatted =
        hotelInvoivesDetails.room_charges?.map((item) => ({
          ...item,
          date: item.date
            ? new Date(item.date).toISOString().split("T")[0]
            : "",
        })) || [];

      setEditedRoomCharges(formatted);
      setCustomer(hotelInvoivesDetails.customer || {});
      setPaymentStatus(hotelInvoivesDetails.pmt_status || "unpaid");
      setBookingDetails({
        checkin_date: hotelInvoivesDetails.checkin_date
          ? moment(hotelInvoivesDetails.checkin_date * 1000).format(
              "YYYY-MM-DD"
            )
          : "",
        checkout_date: hotelInvoivesDetails.checkout_date
          ? moment(hotelInvoivesDetails.checkout_date * 1000).format(
              "YYYY-MM-DD"
            )
          : "",
        booking_id: hotelInvoivesDetails.booking_id || "",
        from: hotelInvoivesDetails.from || "",
        inv_no: hotelInvoivesDetails.inv_no || "",
        paid_amount: hotelInvoivesDetails.paid_amount || 0,
      });
    }
  }, [hotelInvoivesDetails]);

  useEffect(() => {
    let amount = 0;
    let sgst = 0;
    let cgst = 0;

    editedRoomCharges.forEach((item) => {
      const isTaxRow = (item.sgst || 0) > 0 || (item.cgst || 0) > 0;

      if (!isTaxRow) {
        amount += item.amount || 0;
      }

      sgst += item.sgst || 0;
      cgst += item.cgst || 0;
    });

    setTotals({
      amount: parseFloat(amount.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      total: parseFloat((amount + sgst + cgst).toFixed(2)),
    });
  }, [editedRoomCharges]);

  const pdfData = useMemo(() => {
    const sortedRoomCharges = [...editedRoomCharges].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    return {
      ...hotelInvoivesDetails,
      ...totals,
      room_charges: sortedRoomCharges,
      ...bookingDetails,
      customer,
      pmt_status: paymentStatus,
      hotelDetails: {
        name: hotelProfile?.name || "",
        address: hotelProfile?.address || "",
        phone: hotelProfile?.phone || "",
        mobile: hotelProfile?.mobile || "",
        email: hotelProfile?.email || "",
        gstin: hotelProfile?.gstin || "",
        logo: hotelProfile?.logo || "",
        bank_details: hotelProfile?.bank_details || {},
        cin: hotelProfile?.cin || "",
        pan: hotelProfile?.pan || "",
        trade_name: hotelProfile?.trade_name || "",
        gst_enabled: hotelProfile?.gst_enabled || false,
      },
      checkin_date: bookingDetails.checkin_date
        ? Math.floor(new Date(bookingDetails.checkin_date).getTime() / 1000)
        : null,
      checkout_date: bookingDetails.checkout_date
        ? Math.floor(new Date(bookingDetails.checkout_date).getTime() / 1000)
        : null,
    };
  }, [
    hotelInvoivesDetails,
    totals,
    editedRoomCharges,
    customer,
    paymentStatus,
    hotelProfile,
    bookingDetails,
  ]);

  const { inv_no, inv_date, paid_amount } = hotelInvoivesDetails || {};

  // Calculate remaining balance and check if settlement is allowed
  const remainingBalance = totals.total - (paid_amount || 0);
  const canSettle = remainingBalance > 0 && paymentStatus !== "paid";

  const handleFieldChange = (index, field, value) => {
    const updated = [...editedRoomCharges];
    if (!updated[index]) return;

    updated[index][field] = value;

    if (field === "amount") {
      const item = updated[index];
      if (!item) return;
      if (item.cgst) {
        updated[index]["cgst"] = value;
      }
      if (item.sgst) {
        updated[index]["sgst"] = value;
      }
    }

    setEditedRoomCharges(updated);
  };

  const handleBookingChange = (field, value) => {
    setBookingDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (field, value) => {
    setCustomer((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updatedInvoice = {
      ...hotelInvoivesDetails,
      _id: id,
      customer,
      pmt_status: paymentStatus,
      room_charges: editedRoomCharges,
      ...totals,
      ...bookingDetails,
      checkin_date: bookingDetails.checkin_date
        ? Math.floor(new Date(bookingDetails.checkin_date).getTime() / 1000)
        : null,
      checkout_date: bookingDetails.checkout_date
        ? Math.floor(new Date(bookingDetails.checkout_date).getTime() / 1000)
        : null,
    };

    dispatch(updateHotelInvoicesDetails(updatedInvoice));
  };

  const formatDate = (dateValue) =>
    new Date(dateValue).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatDateTime = (dateValue) =>
    new Date(dateValue).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "partially_paid":
        return "warning";
      case "unpaid":
        return "error";
      case "credit":
        return "info";
      default:
        return "default";
    }
  };

  const getPaymentModeLabel = (mode) => {
    return mode.replace("_", " ").toUpperCase();
  };

  if (!hotelInvoivesDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header Section */}
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <Typography variant="h5" gutterBottom>
            Invoice #{inv_no}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Date: {inv_date ? formatDate(inv_date * 1000) : "N/A"}
          </Typography>
          <Chip
            label={paymentStatus.replace("_", " ").toUpperCase()}
            color={getPaymentStatusColor(paymentStatus)}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>

            {canSettle && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentIcon />}
                onClick={() => setShowSettlement(true)}
              >
                Settle Payment
              </Button>
            )}

            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => setShowPreview(true)}
            >
              Preview PDF
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Customer and Booking Details Section */}
      <Grid container spacing={3}>
        {/* Left Column - Customer Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Guest Details:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                value={customer.name || ""}
                onChange={(e) => handleCustomerChange("name", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile"
                value={customer.mobile || ""}
                onChange={(e) => handleCustomerChange("mobile", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                value={customer.email || ""}
                onChange={(e) => handleCustomerChange("email", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={customer.address || ""}
                onChange={(e) =>
                  handleCustomerChange("address", e.target.value)
                }
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="GST No"
                value={customer.gst_no || ""}
                onChange={(e) => handleCustomerChange("gst_no", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - Booking Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Booking Details:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                type="date"
                label="Check-in"
                value={bookingDetails.checkin_date}
                onChange={(e) =>
                  handleBookingChange("checkin_date", e.target.value)
                }
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="date"
                label="Check-out"
                value={bookingDetails.checkout_date}
                onChange={(e) =>
                  handleBookingChange("checkout_date", e.target.value)
                }
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Booking ID"
                value={bookingDetails.booking_id}
                onChange={(e) =>
                  handleBookingChange("booking_id", e.target.value)
                }
                size="small"
                fullWidth
                disabled={true}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Booking From"
                value={bookingDetails.from}
                onChange={(e) => handleBookingChange("from", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Invoice No"
                value={bookingDetails.inv_no}
                onChange={(e) => handleBookingChange("inv_no", e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="number"
                label="Advance Paid Amount"
                value={bookingDetails.paid_amount}
                onChange={(e) =>
                  handleBookingChange(
                    "paid_amount",
                    parseFloat(e.target.value) || 0
                  )
                }
                size="small"
                fullWidth
                disabled={true}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-status-label">
                  Payment Status
                </InputLabel>
                <Select
                  labelId="payment-status-label"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  label="Payment Status"
                  disabled={paymentStatus === "paid"}
                >
                  <MenuItem value="paid" disabled>Paid</MenuItem>
                  <MenuItem value="unpaid" disabled>Unpaid</MenuItem>
                  <MenuItem value="partially_paid" disabled>Partially Paid</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Room Charges Section */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Room Charges & Services
      </Typography>
      <TableContainer component={Paper} sx={{ bgcolor: colors.primary[400], p: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell sx={{ width: "40%" }}>Particulars</TableCell>
              <TableCell>#SAC</TableCell>
              <TableCell>Amount (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editedRoomCharges.map((item, index) => (
              <TableRow key={item.groupId ? `${item.groupId}-${index}` : index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField
                    type="date"
                    value={item.date}
                    onChange={(e) =>
                      handleFieldChange(index, "date", e.target.value)
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={item.particulars || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "particulars", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={item.sac_code || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "sac_code", e.target.value)
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={item.amount || 0}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        "amount",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    disabled={true}
                    size="small"
                    fullWidth
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Settlement History Section */}
      {hotelInvoivesDetails?.settlements &&
        hotelInvoivesDetails.settlements.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
              <ReceiptIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Settlement History
            </Typography>
            <TableContainer
              component={Paper}
              sx={{ bgcolor: colors.primary[400] }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Settlement Date</TableCell>
                    <TableCell>Amount (₹)</TableCell>
                    <TableCell>Payment Mode</TableCell>
                    <TableCell>Transaction Ref</TableCell>
                    <TableCell>Settled By</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Recorded At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hotelInvoivesDetails.settlements.map((settlement, index) => (
                    <TableRow key={settlement._id || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {settlement.settlement_date
                          ? new Date(
                              settlement.settlement_date * 1000
                            ).toLocaleDateString("en-IN", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="success.main"
                        >
                          ₹{settlement.amount?.toFixed(2) || "0.00"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPaymentModeLabel(
                            settlement.payment_mode || "cash"
                          )}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {settlement.transaction_ref || "-"}
                      </TableCell>
                      <TableCell>{settlement.settled_by || "-"}</TableCell>
                      <TableCell>
                        {settlement.remarks || "-"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {settlement.createdAt
                            ? formatDateTime(settlement.createdAt)
                            : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

      {/* Save Button and Totals Section */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        mt={3}
      >
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" sx={{ bgcolor: colors.primary[400], p: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography>Subtotal:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.amount.toFixed(2)}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography>SGST:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.sgst.toFixed(2)}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography>CGST:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.cgst.toFixed(2)}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="h6">Grand Total:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="h6">
                  ₹{totals.total.toFixed(2)}
                </Typography>
              </Grid>

              {paid_amount > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography>Total Paid:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography color="success.main">
                      - ₹{paid_amount.toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="h6">Balance Due:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography
                      variant="h6"
                      color={remainingBalance > 0 ? "error" : "success.main"}
                    >
                      ₹{remainingBalance.toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Settlement Dialog */}
      {showSettlement && (
        <SettlementDialog
          open={showSettlement}
          onClose={() => setShowSettlement(false)}
          invoice={hotelInvoivesDetails}
        />
      )}

      {/* PDF Preview Dialog */}
      <Dialog
        maxWidth="lg"
        fullWidth
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <DialogActions sx={{ justifyContent: "space-between", p: "8px 24px" }}>
          <Typography variant="h6">Invoice Preview</Typography>
          <IconButton onClick={() => setShowPreview(false)}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 0, height: "100vh" }}>
          <PDFViewer width="100%" height="100%">
            <InvoicePDF data={pdfData} />
          </PDFViewer>
        </DialogContent>
      </Dialog>
    </Box>
  );
}